import { motion } from "framer-motion";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";

const APP_STORE_URL = "https://apps.apple.com/us/app/teamup/id1521654234";

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-7 w-7 fill-current">
    <path d="M17.05 12.54c-.03-2.89 2.36-4.27 2.47-4.34-1.35-1.97-3.44-2.24-4.18-2.27-1.78-.18-3.47 1.05-4.37 1.05-.9 0-2.29-1.02-3.77-1-1.94.03-3.72 1.13-4.72 2.86-2.01 3.49-.51 8.66 1.45 11.5.96 1.39 2.1 2.95 3.6 2.89 1.45-.06 2-.93 3.75-.93s2.25.93 3.78.9c1.56-.03 2.55-1.41 3.5-2.81 1.1-1.61 1.55-3.17 1.58-3.25-.03-.02-3.03-1.16-3.09-4.6zM14.15 4.06c.8-.97 1.34-2.32 1.19-3.66-1.15.05-2.55.77-3.38 1.73-.74.86-1.39 2.23-1.22 3.55 1.29.1 2.6-.65 3.41-1.62z" />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" className="h-6 w-6 fill-current">
    <path d="M3.6 2.3c-.34.36-.55.86-.55 1.46v16.5c0 .6.2 1.1.55 1.45l.08.07 9.25-9.25v-.17L3.68 2.23l-.08.07zm12.2 9.18l-2.7-2.7 3.3-3.32 3.96 2.25c1.13.64 1.13 1.7 0 2.35l-3.96 2.25-.6-.83zm-.65.65l-2.9 2.9-8.6 4.88 11.5-7.78zm-2.9-4.52l-11.5-7.78 8.6 4.89 2.9 2.89z" />
  </svg>
);

const badgeClass =
  "flex items-center gap-4 rounded-2xl border border-white/15 bg-pitch-card px-7 py-4 text-left text-white transition-colors duration-300 hover:border-brand/50 hover:shadow-[0_0_40px_rgba(236,93,24,0.22)]";

const badgeMotion = {
  whileHover: { scale: 1.04 },
  whileTap: { scale: 0.96 },
  transition: { type: "spring", stiffness: 400, damping: 22 },
};

const BadgeInner = ({ icon, top, bottom }) => (
  <>
    {icon}
    <span>
      <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-white/55">{top}</span>
      <span className="block text-xl font-semibold leading-tight">{bottom}</span>
    </span>
  </>
);

export const DownloadSection = () => {
  const { t } = useLanguage();
  const notify = () => toast(t.download.toast);

  return (
    <section id="download" data-testid="teamup-download-section" className="relative overflow-hidden">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[820px] -translate-x-1/2 rounded-full bg-brand/[0.08] blur-[120px]" />

      <div className="relative mx-auto max-w-7xl px-5 py-28 sm:px-8 md:py-36">
        <div className="grid items-center gap-14 md:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="mb-5 font-mono text-[11px] uppercase tracking-[0.3em] text-brand sm:text-xs">{t.download.eyebrow}</p>
            <h2 className="font-display text-3xl font-extrabold uppercase leading-[1.05] tracking-tight text-white sm:text-5xl">
              {t.download.heading}
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-mist sm:text-lg">{t.download.text}</p>

            <div className="mt-10 flex flex-wrap gap-4">
              <motion.a
                data-testid="teamup-app-store-button"
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={badgeClass}
                {...badgeMotion}
              >
                <BadgeInner icon={<AppleIcon />} top={t.download.appStoreTop} bottom={t.download.appStoreBottom} />
              </motion.a>
              <motion.button
                type="button"
                data-testid="teamup-google-play-button"
                onClick={notify}
                className={badgeClass}
                {...badgeMotion}
              >
                <BadgeInner icon={<PlayIcon />} top={t.download.playTop} bottom={t.download.playBottom} />
              </motion.button>
            </div>

            <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-white/35">{t.download.note}</p>
            <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-brand/70">{t.download.domain}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40, rotate: 1.5 }}
            whileInView={{ opacity: 1, y: 0, rotate: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="overflow-hidden rounded-2xl border border-white/10">
              <img src="/images/field.jpg" alt="Football pitch at night" loading="lazy" className="h-72 w-full object-cover sm:h-96" />
            </div>
            <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-t from-pitch/60 via-transparent to-transparent" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
